import React, { FC, useCallback } from 'react';
import { AnalyticsEventLocation } from '@mb/lib';
import { getUserLang } from '@mb/lib';
import { useInView } from 'react-intersection-observer';
import { useIntl } from 'react-intl';
import * as Styled from './CollectionCarousel.styles';
import { CollectionItemProps } from './types';
import { goToPageTracking } from '../..//helpers';
import { CURATED_COLLECTIONS_PAGE } from '../../constants/collections';
import {
  pluralize,
  clearAnalyticsEvents,
  sendSelectPromotionAnalyticsEvent,
  formatImageSource,
} from '../../helpers';
import { definedTranslation } from '../../i18n';

const PLACEHOLDER_SRC =
  'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs%3D';

const userLang = getUserLang();

export const CollectionItem: FC<CollectionItemProps> = (props) => {
  const {
    campaign,
    isFeatured,
    responsive,
    sendInViewChange,
    collectionTitle,
    index,
    inView = false,
    eventLocation,
    customWidth,
  } = props;

  const isSearchPage = window.location.pathname === '/search';

  const CATEGORY_LIMIT = isFeatured ? 15 : 6;
  const IMAGE_SIZE = customWidth || (isFeatured ? 564 : 328);
  const materialsCount = campaign?.children_count ?? 0;

  const intl = useIntl();

  const link =
    ENVIRONMENT_REGION === 'EU'
      ? `/${userLang}/${CURATED_COLLECTIONS_PAGE}/${campaign?.url_key}`
      : `/${CURATED_COLLECTIONS_PAGE}/${campaign?.url_key}` || '#';

  const imageSource = formatImageSource(
    campaign ? campaign.thumbnailImageUrl : '',
    IMAGE_SIZE,
    false,
  );
  const { ref: inViewRef } = useInView({
    threshold: 0.5,
    onChange: onInViewChange,
  });

  function onInViewChange(isInView: boolean) {
    sendInViewChange?.(campaign, isInView);
  }

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();

      clearAnalyticsEvents({ ecommerce: null }).then(() => {
        const name = campaign?.name || '';
        sendSelectPromotionAnalyticsEvent(
          collectionTitle,
          {
            name,
            index,
          },
          eventLocation,
        );
      });

      if (eventLocation === AnalyticsEventLocation.HOME) {
        goToPageTracking('collections', link);
      } else {
        window.location.href = link;
      }
    },
    [isSearchPage, eventLocation, campaign?.name, collectionTitle, index, link],
  );

  const materialText = intl?.formatMessage(
    definedTranslation.collections.materialText,
  );
  const materialsText = intl?.formatMessage(
    definedTranslation.collections.materialsText,
  );

  return (
    <Styled.ItemContainer>
      <Styled.Item
        isFeatured={isFeatured}
        responsive={responsive}
        href={link}
        ref={inViewRef}
        onClick={handleClick}
        customWidth={customWidth}
      >
        <Styled.HoverContainer>
          <Styled.HoverContent>
            <Styled.HoverTitle
              variant={isFeatured ? 'h2' : 'body'}
              fontWeight="bold"
            >
              {campaign?.name}
            </Styled.HoverTitle>
            <Styled.HoverSubtitle variant="tiny">
              {`${materialsCount} ${pluralize(
                materialsCount,
                materialText,
                materialsText,
              )}`}
            </Styled.HoverSubtitle>
            <Styled.HoverCategories>
              {campaign?.taxonomy_l2
                ?.slice(0, CATEGORY_LIMIT)
                .map((category) => (
                  <Styled.HoverCategory key={category} variant="tiny">
                    <span>{category}</span>
                  </Styled.HoverCategory>
                ))}
              {campaign?.children_count ||
                (0 > CATEGORY_LIMIT && (
                  <Styled.HoverCategory variant="tiny">
                    +{campaign?.children_count || 0 - CATEGORY_LIMIT}
                  </Styled.HoverCategory>
                ))}
            </Styled.HoverCategories>
          </Styled.HoverContent>
        </Styled.HoverContainer>

        {campaign?.thumbnailImageUrl && (
          <img
            src={inView ? imageSource.src : PLACEHOLDER_SRC}
            srcSet={inView ? imageSource.srcSet : PLACEHOLDER_SRC}
            alt=""
            loading="lazy"
          />
        )}
      </Styled.Item>
    </Styled.ItemContainer>
  );
};
